import React from 'react';
import { Property } from '../../../types';

interface Props {
  formData: Partial<Property>;
  onChange: (field: keyof Property, value: any) => void;
}

const parseNumericArea = (val: any): string | number => {
  if (val === undefined || val === null || val === '') return '';
  if (typeof val === 'number') return val;
  const cleaned = String(val).replace(/m²|\s/g, '').trim();
  const num = parseFloat(cleaned);
  return isNaN(num) ? '' : num;
};

export const PropertySpecsFields: React.FC<Props> = ({ formData, onChange }) => {
  return (
    <div className="space-y-4 text-xs">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <div>
          <label className="font-bold text-gray-700 block mb-1">Habitaciones</label>
          <input
            type="number"
            min="0"
            value={formData.num_habitaciones ?? ''}
            onChange={(e) => onChange('num_habitaciones', Number(e.target.value) || 0)}
            className="w-full p-2 border rounded-lg focus:ring-sky-500"
            placeholder="0"
          />
        </div>
        <div>
          <label className="font-bold text-gray-700 block mb-1">Baños</label>
          <input
            type="number"
            min="0"
            value={formData.num_banos ?? ''}
            onChange={(e) => onChange('num_banos', Number(e.target.value) || 0)}
            className="w-full p-2 border rounded-lg focus:ring-sky-500"
            placeholder="0"
          />
        </div>
        <div>
          <label className="font-bold text-gray-700 block mb-1">Parqueaderos</label>
          <input
            type="number"
            min="0"
            value={formData.num_parqueaderos ?? ''}
            onChange={(e) => onChange('num_parqueaderos', Number(e.target.value) || 0)}
            className="w-full p-2 border rounded-lg focus:ring-sky-500"
            placeholder="0"
          />
        </div>
        <div>
          <label className="font-bold text-gray-700 block mb-1">Ascensores</label>
          <input
            type="number"
            min="0"
            value={formData.ascensor ?? ''}
            onChange={(e) => {
              const val = e.target.value === '' ? null : Math.max(0, parseInt(e.target.value, 10) || 0);
              onChange('ascensor', val);
              onChange('num_ascensores' as any, val);
            }}
            className="w-full p-2 border rounded-lg focus:ring-sky-500 font-medium"
            placeholder="0 (Sin ascensor)"
          />
        </div>
        <div className="col-span-2 sm:col-span-1 lg:col-span-1">
          <label className="font-bold text-gray-700 block mb-1">Tipo Parqueadero</label>
          <input
            type="text"
            value={formData.tipo_parqueadero || ''}
            onChange={(e) => onChange('tipo_parqueadero', e.target.value)}
            className="w-full p-2 border rounded-lg focus:ring-sky-500"
            placeholder="Ej: Cubierto, Privado"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div>
          <label className="font-bold text-gray-700 block mb-1">Área Construida (m²)</label>
          <input
            type="number"
            value={parseNumericArea(formData.area_construida ?? formData.area_construida_original)}
            onChange={(e) => {
              const v = e.target.value === '' ? '' : Number(e.target.value) || 0;
              onChange('area_construida', v === '' ? 0 : v);
              onChange('area_construida_original', v !== '' && v > 0 ? `${v} m²` : '');
            }}
            className="w-full p-2 border rounded-lg focus:ring-sky-500"
            placeholder="0"
          />
        </div>
        <div>
          <label className="font-bold text-gray-700 block mb-1">Área Privada (m²)</label>
          <input
            type="number"
            value={parseNumericArea(formData.area_privada)}
            onChange={(e) => {
              const v = e.target.value === '' ? '' : Number(e.target.value) || 0;
              onChange('area_privada', v !== '' && v > 0 ? `${v} m²` : null);
            }}
            className="w-full p-2 border rounded-lg focus:ring-sky-500"
            placeholder="0"
          />
        </div>
        <div>
          <label className="font-bold text-gray-700 block mb-1">Estrato</label>
          <input
            type="text"
            value={formData.estrato || ''}
            onChange={(e) => onChange('estrato', e.target.value)}
            className="w-full p-2 border rounded-lg focus:ring-sky-500"
            placeholder="Ej: 4"
          />
        </div>
        <div>
          <label className="font-bold text-gray-700 block mb-1">Antigüedad (Años)</label>
          <input
            type="number"
            value={formData.antiguedad ?? ''}
            onChange={(e) => {
              const v = e.target.value === '' ? null : Number(e.target.value) || 0;
              onChange('antiguedad', v);
              if (v !== null && v >= 0) {
                onChange('constructionYear', new Date().getFullYear() - v);
              }
            }}
            className="w-full p-2 border rounded-lg focus:ring-sky-500"
            placeholder="Ej: 5"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <label className="font-bold text-gray-700 block mb-1">Piso</label>
          <input
            type="text"
            value={formData.piso || ''}
            onChange={(e) => onChange('piso', e.target.value)}
            className="w-full p-2 border rounded-lg focus:ring-sky-500"
            placeholder="Ej: 3"
          />
        </div>
        <div>
          <label className="font-bold text-gray-700 block mb-1">Prioridad Global</label>
          <input
            type="number"
            value={formData.prioridad_global ?? 10}
            onChange={(e) => onChange('prioridad_global', Number(e.target.value) || 10)}
            className="w-full p-2 border rounded-lg focus:ring-sky-500"
            placeholder="10"
          />
        </div>
        <div>
          <label className="font-bold text-gray-700 block mb-1">Disponibilidad Visitas</label>
          <input
            type="text"
            value={formData.disponibilidad_visitas || ''}
            onChange={(e) => onChange('disponibilidad_visitas', e.target.value)}
            className="w-full p-2 border rounded-lg focus:ring-sky-500"
            placeholder="Ej: Lunes a Viernes 9am-5pm"
          />
        </div>
      </div>

      <div className="flex items-center gap-6 pt-2">
        <label className="flex items-center gap-2 cursor-pointer font-bold text-gray-700">
          <input
            type="checkbox"
            checked={!!formData.es_visible_broker}
            onChange={(e) => onChange('es_visible_broker', e.target.checked)}
            className="w-4 h-4 rounded text-sky-600 focus:ring-sky-500"
          />
          Visible para Brokers
        </label>
        <label className="flex items-center gap-2 cursor-pointer font-bold text-gray-700">
          <input
            type="checkbox"
            checked={!!formData.destacado}
            onChange={(e) => onChange('destacado', e.target.checked)}
            className="w-4 h-4 rounded text-amber-500 focus:ring-amber-400"
          />
          Destacar Inmueble
        </label>
      </div>
    </div>
  );
};
